import { useQuery } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';

export interface WorkoutExercise {
  _id: string;
  en_display_name: string;
  image_male_url: string;
  image_female_url: string;
}

export interface ClientProgramTabResponse {
  status: 'ok' | 'error';
  data: {
    intensity: string;
    equipments: string[];
    primaryGoal: string;
    priorityFocus: string[];
    backgroundImageGender: 'male' | 'female';
    goalApproach: string;
    goalMethod: string[];
    timeMin: number;
    currentWeek: string;
    totalWeeks: string;
    frequency: string;
    status?: 'Active' | 'Inactive' | 'draft' | string;
    todayWorkout: {
      week: number;
      day: string;
      date: string;
      exercises: WorkoutExercise[];
    };
    planCalendar: {
      reminderTime: string;
      workoutDays: number[];
      calendarWeek: any[];
    };
    workoutPerformance: { label: string; value: number }[];
    adherence: {
      workoutCompletion: string;
      workoutCompletionNum: number;
      avgTimeAdherence: string;
      avgTimeAdherenceNum: number;
      planRespect: string;
      planRespectNum: number;
      planRespectTotal: number;
    };
    weeksData?: {
      week: number;
      days: {
        dayLabel: string;
        dateYmd: string;
        exercises: WorkoutExercise[];
      }[];
    }[];
    completedWorkoutsYmds?: string[];
    datetimeList?: any[];
    progress?: Record<string, any>;
    programId?: string;
    isManually?: boolean;
    isAIGenerate?: boolean;
    isDraft?: boolean;
    generatorType?: string;
  };
  error?: string;
  errorCode?: string;
}

const getClientProgramTab = async (
  clientId: string,
  trainerId?: string,
): Promise<ClientProgramTabResponse['data']> => {
  const queryParams = new URLSearchParams({ clientId });
  if (trainerId) queryParams.append('trainerId', trainerId);

  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_CLIENT_PROGRAM_TAB}?${queryParams.toString()}`;
  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to fetch program tab data');
  }

  const data: ClientProgramTabResponse = await response.json();
  if (data.status === 'error') {
    throw new Error(data.error || 'Failed to fetch program tab data');
  }
  return data.data;
};

export const useGetClientProgramTab = (clientId: string, trainerId?: string) => {
  return useQuery({
    queryKey: ['clientProgramTab', clientId, trainerId],
    queryFn: () => getClientProgramTab(clientId, trainerId),
    enabled: !!clientId,
  });
};
